/**
 * Website Make changes — the authoring calls (backend `routers/site_edit.py`).
 * Platform builders only; never reachable on a bound domain.
 */

import api from '@/api/client'
import type { PageModel } from '@/pages/designers/page/types'
import type { SiteMenusSpec } from '@twasta/page-schema'

export interface SiteEditPage {
  obj_name: string
  title: string
  /** The site's designed header / footer / pop-up — not a page in its own right. */
  chrome: boolean
  in_menu: boolean
}

export interface SiteEditSettings {
  title: string
  header_scroll: 'static' | 'sticky' | 'auto-hide'
  menus: SiteMenusSpec
  theme: { ref?: string; preset?: string }
  header_page_obj_name: string
  footer_page_obj_name: string
}

export interface SiteEditSite {
  site_id: string
  /** The website node's obj_name — what a theme is applied to. */
  obj_name: string
  title: string
  node_title: string
  slug: string
  settings: SiteEditSettings
  pages: SiteEditPage[]
  hash: string
}

export interface StoredPage { page: PageModel; hash: string }

const base = (project: string) => `/site-edit/${encodeURIComponent(project)}`

/** A backend started before `/api/site-edit` existed answers with the app's
 *  HTML page — say so instead of failing on a missing field. */
function json<T>(data: unknown): T {
  if (!data || typeof data !== 'object') {
    throw new Error('This server does not offer website Make changes yet — restart the backend and try again.')
  }
  return data as T
}

export async function getEditSite(project: string, siteId: string): Promise<SiteEditSite> {
  const { data } = await api.get(`${base(project)}/site/${encodeURIComponent(siteId)}`)
  return json(data)
}

export async function patchSiteSettings(project: string, siteId: string,
  set: Partial<Record<'title' | 'header_scroll' | 'menus', unknown>>, baseHash: string): Promise<SiteEditSite> {
  const { data } = await api.patch(`${base(project)}/site/${encodeURIComponent(siteId)}/settings`, { set, base_hash: baseHash })
  return json(data)
}

export async function addSitePage(project: string, siteId: string,
  body: { title: string; start: string; in_menu: boolean }): Promise<{ obj_name: string; node_id: string; site: SiteEditSite }> {
  const { data } = await api.post(`${base(project)}/site/${encodeURIComponent(siteId)}/pages`, body)
  return json(data)
}

export async function getEditPage(project: string, objName: string): Promise<StoredPage> {
  const { data } = await api.get(`${base(project)}/page/${encodeURIComponent(objName)}`)
  return json(data)
}

export async function putEditPage(project: string, objName: string, page: PageModel, baseHash: string): Promise<StoredPage> {
  const { data } = await api.put(`${base(project)}/page/${encodeURIComponent(objName)}`, { page, base_hash: baseHash })
  return json(data)
}
